import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/lib/toast";
import { Goal } from "@/types/database";

interface SetGoalColumnDialogProps {
  isOpen: boolean;
  onClose: () => void;
  columnId: string;
  columnName: string;
  boardId: string;
  currentGoalId?: string;
  onSuccess: () => void;
}

export function SetGoalColumnDialog({
  isOpen,
  onClose,
  columnId,
  columnName,
  boardId,
  currentGoalId,
  onSuccess,
}: SetGoalColumnDialogProps) {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<string>(currentGoalId || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGoals, setIsLoadingGoals] = useState(true);

  const loadGoals = async () => {
    if (!user) return;
    
    try {
      setIsLoadingGoals(true);
      
      // Buscar o board para pegar o client_id
      const { data: boardData, error: boardError } = await supabase
        .from('kanban_boards')
        .select('client_id, clients(name), company_id')
        .eq('id', boardId)
        .single();

      if (boardError) {
        console.error("Error loading board:", boardError);
        toast.error("Erro ao carregar informações do quadro");
        return;
      }

      if (!boardData?.client_id) {
        toast.error("Board não está vinculado a um cliente");
        return;
      }

      let targetCompanyId = boardData.company_id;

      if (!targetCompanyId) {
        // Fallback para buscar company_id do usuário
        const { data: collaboratorData } = await supabase
          .from('collaborators')
          .select('company_id')
          .eq('collaborator_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (collaboratorData) {
          targetCompanyId = collaboratorData.company_id;
        } else {
          const { data: userData } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', user.id)
            .single();
          targetCompanyId = userData?.company_id || null;
        }
      }

      if (!targetCompanyId) {
        toast.error("Empresa não encontrada");
        return;
      }

      // Buscar metas de postagens para esta empresa
      const { data: goalsData, error: goalsError } = await supabase
        .from('goals')
        .select('*')
        .eq('company_id', targetCompanyId)
        .eq('goal_type', 'postagens')
        .eq('completed', false)
        .order('created_at', { ascending: false });

      if (goalsError) {
        console.error("Error loading goals:", goalsError);
        toast.error("Erro ao carregar metas");
        return;
      }

      // Filtrar metas que sejam para este cliente e que o usuário pode ver
      const clientName = (boardData.clients as any)?.name;
      const filteredGoals = (goalsData || []).filter(goal => {
        // Verificar se a meta é para este cliente
        const isForThisClient = goal.client_name === clientName;
        
        // Verificar se o usuário pode ver esta meta
        const canViewGoal = goal.user_id === user.id || 
                           (goal.visible_to && Array.isArray(goal.visible_to) && goal.visible_to.includes(user.id));
        
        return isForThisClient && canViewGoal;
      });

      console.log("Goals found for client:", clientName, filteredGoals);
      setGoals(filteredGoals as Goal[]);
    } catch (error) {
      console.error("Error loading goals:", error);
      toast.error("Erro ao carregar metas");
    } finally {
      setIsLoadingGoals(false);
    }
  };

  const handleSave = async () => {
    if (!selectedGoalId) {
      toast.error("Selecione uma meta");
      return;
    }

    try {
      setIsLoading(true);

      // Primeiro, remover qualquer outra coluna que possa estar vinculada a esta meta
      await supabase
        .from('kanban_columns')
        .update({
          is_goal_column: false,
          goal_id: null
        })
        .eq('goal_id', selectedGoalId);

      // Atualizar a coluna para marcar como coluna meta
      const { error } = await supabase
        .from('kanban_columns')
        .update({
          is_goal_column: true,
          goal_id: selectedGoalId
        })
        .eq('id', columnId);

      if (error) {
        console.error("Error updating column:", error);
        toast.error("Erro ao definir coluna como meta");
        return;
      }

      // Calcular progresso inicial da meta baseado nos cartões já existentes
      const { data: cardsData, error: cardsError } = await supabase
        .from('kanban_cards')
        .select('id')
        .eq('column_id', columnId)
        .eq('is_archived', false);

      if (!cardsError && cardsData) {
        const { data: goalData, error: goalError } = await supabase
          .from('goals')
          .select('target_value')
          .eq('id', selectedGoalId)
          .single();

        if (!goalError && goalData) {
          const currentValue = cardsData.length;
          const isCompleted = currentValue >= goalData.target_value;

          await supabase
            .from('goals')
            .update({
              current_value: currentValue,
              completed: isCompleted,
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedGoalId);
        }
      }

      toast.success("Coluna definida como coluna meta!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error setting goal column:", error);
      toast.error("Erro ao definir coluna como meta");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveGoal = async () => {
    try {
      setIsLoading(true);

      // Remover vinculação da meta
      const { error } = await supabase
        .from('kanban_columns')
        .update({
          is_goal_column: false,
          goal_id: null
        })
        .eq('id', columnId);

      if (error) {
        console.error("Error removing goal from column:", error);
        toast.error("Erro ao remover meta da coluna");
        return;
      }

      toast.success("Meta removida da coluna!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error removing goal from column:", error);
      toast.error("Erro ao remover meta da coluna");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadGoals();
      setSelectedGoalId(currentGoalId || "");
    }
  }, [isOpen, currentGoalId]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {currentGoalId ? "Gerenciar Meta da Coluna" : "Definir como Coluna Meta"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Coluna: <strong>{columnName}</strong></Label>
            <p className="text-sm text-muted-foreground">
              Quando cartões forem movidos para esta coluna, o progresso da meta será atualizado automaticamente.
            </p>
          </div>

          {isLoadingGoals ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : goals.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                Nenhuma meta de postagens encontrada para este cliente.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Crie uma meta de postagens primeiro na página de Metas.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="goal">Meta de Postagens</Label>
              <Select value={selectedGoalId} onValueChange={setSelectedGoalId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma meta" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((goal) => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.title} ({goal.current_value || 0}/{goal.target_value})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            
            {currentGoalId && (
              <Button 
                type="button" 
                variant="destructive" 
                onClick={handleRemoveGoal}
                disabled={isLoading}
              >
                {isLoading ? "Removendo..." : "Remover Meta"}
              </Button>
            )}
            
            {goals.length > 0 && (
              <Button 
                type="button" 
                onClick={handleSave}
                disabled={isLoading || !selectedGoalId}
              >
                {isLoading ? "Salvando..." : currentGoalId ? "Atualizar" : "Definir Meta"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
